<template>
  <FormStepper
    v-if="isMounted"
    ref="formStepperRef"
    :is-stepper-shown="false"
    :valid-step="0"
    wizard-title="Geräte Service"
    :enable-save-button="!showReport && true"
    :disable-navigational-buttons="false"
    :disable-auto-mount="false"
    @close-stepper="closeFormDialog"
    @on-tab-change="onTabChange"
  >
    <stepper-content v-if="!showReport" :selected="true">
      <DeviceVerificationForm
        v-model:device="device"
        v-model:device-event="deviceEvent"
        v-model:installation-point="installationPoint"
        v-model:tkd-service="TkdService"
        v-model:images="images"
        @update:model-value="validateCurrentStep"
      />
    </stepper-content>

    <stepper-content v-if="!showReport">
      <BaseToggle
        v-model:is-checked="isTKDPossible"
        label="Kein Service möglich"
        test-id="failedTKD"
        :is-inverted="true"
      />
      <NoTKDPossibleForm
        v-if="!isTKDPossible"
        v-model:current-device="device"
        v-model:new-device="newDevice"
        v-model:new-battery-pack="newBatteryPack"
        v-model:installation-point="installationPoint"
        v-model:device-service="TkdService.deviceService"
        v-model:accessory-service-list="accessoryServiceList"
        @update:model-value="validateCurrentStep"
      />
      <DeviceServiceForm
        v-if="isTKDPossible"
        v-model:current-device="device"
        v-model:new-device="newDevice"
        v-model:new-battery-pack="newBatteryPack"
        v-model:installation-point="installationPoint"
        v-model:device-service="TkdService.deviceService"
        v-model:accessory-service-list="accessoryServiceList"
        @update:model-value="validateCurrentStep"
      />
    </stepper-content>

    <stepper-content v-if="!showReport">
      <DeviceTicketForm
        v-model:tkd-service="TkdService"
        v-model:installation-point="installationPoint"
        v-model:images="images"
        :suggested-ticket-numbers="props.suggestedTicketNumbers"
        :old-device-serial-number="props.oldDeviceSerialNumber"
        @update:model-value="validateCurrentStep"
      />
    </stepper-content>

    <stepper-content v-else>
      <TkdServiceReport
        v-model:tkd-service="TkdService"
        v-model:installation-point="installationPoint"
        v-model:images="images"
        v-model:current-device="device"
      />
    </stepper-content>
  </FormStepper>
</template>
<script setup lang="ts">
import { DeviceClasses } from '@/models/devices/Device';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import { DeviceEventParams } from '@/models/installationPoint/DeviceEvent';
import DeviceVerificationForm from '@/components/Forms/TkdServiceComponents/DeviceVerificationForm.vue';
import DeviceServiceForm from '@/components/Forms/TkdServiceComponents/DeviceServiceForm.vue';
import DeviceTicketForm from '@/components/Forms/TkdServiceComponents/DeviceTicketForm.vue';
import TkdServiceReport from '@/components/Forms/TkdServiceComponents/TkdServiceReport.vue';
import FormStepper from '@/components/UI/Stepper/FormStepper.vue';
import StepperContent from '@/components/UI/Stepper/StepperContent.vue';
import { onMounted, ref, watch } from 'vue';
import { BatteryPack } from '@/models/installationPoint/Accessory';
import { TKDService } from '@/models/installationPoint/TKDService';

import BaseToggle from '@/components/UI/Switch/BaseToggle.vue';
import NoTKDPossibleForm from '@/components/Forms/TkdServiceComponents/NoTKDPossibleForm.vue';
import { AccessoryService } from '@/models/installationPoint/AccessoryService';
import { Image } from '@/models/Image';

const props = defineProps<{
  suggestedTicketNumbers: Array<string>;
  showReport: boolean;
  oldDeviceSerialNumber: string;
}>();

const device = defineModel<DeviceClasses>('currentDevice');
const deviceEvent = defineModel<DeviceEventParams>('currentDeviceEvent');
const installationPoint = defineModel<InstallationPoint>('installationPoint');
const TkdService = defineModel<TKDService>('tkdService');
const newDevice = defineModel<InstallationPoint>('newDevice');
const newBatteryPack = defineModel<BatteryPack>('newBatteryPack');
const accessoryServiceList = defineModel<Record<string, AccessoryService>>(
  'accessoryServiceList'
);
const images = defineModel<Image[]>('images');

const isMounted = ref(false);
const isTKDPossible = ref(true);

const emit = defineEmits(['on-save']);

const closeFormDialog = () => {
  emit('on-save');
};

const onTabChange = (index: number) => {
  return true;
};

onMounted(() => {
  isMounted.value = true;
});

watch(isTKDPossible, () => {
  if (TkdService.value?.deviceService) {
    TkdService.value.deviceService.type = undefined;
    TkdService.value.deviceService.reason = undefined;
    TkdService.value.deviceService.note = undefined;
  }
  validateCurrentStep(false);
});

/**
 * Functions provided by
 * {@link FormStepper#validateStep}
 */
const formStepperRef = ref(undefined);

const validateCurrentStep = (isStepValid: boolean) => {
  formStepperRef.value?.validateStep(isStepValid);
};
</script>
